import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Crown, Sparkles, ShieldCheck, AlertTriangle, X } from "lucide-react";
import Layout from "../components/Layout";
import { SquareSubscribeModal } from "../components/SquareSubscribeModal";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

function formatPrice(cents) {
  if (!cents) return "Free";
  return cents % 100 === 0 ? `$${(cents / 100).toFixed(0)}` : `$${(cents / 100).toFixed(2)}`;
}

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = React.useState(null);
  const [me, setMe] = React.useState(null);
  const [period, setPeriod] = React.useState("monthly");
  const [loading, setLoading] = React.useState(true);
  const [acting, setActing] = React.useState(false);
  const [squareConfig, setSquareConfig] = React.useState(null);
  const [modalTier, setModalTier] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      api.post("/billing/sync").catch(() => {});
      const [cfg, mine, sq] = await Promise.all([
        api.get("/billing/config"),
        api.get("/billing/me"),
        api.get("/billing/square/config").catch(() => ({ data: { available: false } })),
      ]);
      setConfig(cfg.data);
      setMe(mine.data);
      setSquareConfig(sq.data);
    } catch (e) {
      toast.error("Could not load billing info.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const onSubscribe = async (tier) => {
    if (me?.verification?.status !== "verified") {
      toast.message("Please complete professional verification first.");
      navigate("/billing/verify");
      return;
    }
    const t = (config?.tiers || {})[tier] || {};
    if (squareConfig?.available) {
      setModalTier({ code: tier, label: t.label || tier, priceCents: t.monthly_price_cents });
      return;
    }
    setActing(true);
    try {
      const r = await api.post("/billing/checkout-link", { tier, billing_period: period });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "checkout_unavailable") {
        toast.error("Online checkout is not yet enabled. Contact an admin to grant access.");
      } else {
        toast.error(detail || "Could not start checkout.");
      }
    } finally {
      setActing(false);
    }
  };

  const onCancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the period ends.")) return;
    setActing(true);
    try {
      await api.post("/billing/cancel");
      await load();
      await refreshUser();
      toast.success("Subscription canceled. You retain access until the end of your billing period.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel.");
    } finally {
      setActing(false);
    }
  };

  const ackDowngrade = async () => {
    try {
      await api.post("/billing/downgrade-notice/ack");
      await load();
      await refreshUser();
    } catch { /* silent */ }
  };

  if (loading || !config || !me) {
    return <Layout><div className="text-ink-500 animate-pulse" data-testid="billing-loading">Loading billing…</div></Layout>;
  }

  const tiers = config.tiers || {};
  const foundingVisible = config.founding_open || effectiveIsFounding(me);
  const tierOrder = ["free", ...(foundingVisible ? ["founding"] : []), "pro", "clinic"];
  const effective = me.effective_tier || "free";
  const isEarlyAdopter = me.early_adopter?.granted;
  const inAppCheckout = squareConfig?.available;
  const checkoutAvailable = inAppCheckout || config.checkout_available;
  const status = me.subscription?.status;

  return (
    <Layout>
      <div data-testid="billing-page" className="max-w-5xl">
        <div className="label-overline mb-2">Account</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-3">Billing & plan</h1>
        <p className="text-ink-700 mb-6 max-w-2xl">
          Your client records, sessions, and clinical notes are <strong>always preserved</strong>, regardless of your plan.
          Premium AI features and clinical tools are gated by subscription.
        </p>

        {/* Downgrade notice */}
        {me.downgrade_notice && (
          <div data-testid="downgrade-notice" className="rounded-3xl border border-terracotta-500/40 bg-terracotta-500/5 p-5 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-terracotta-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-ink-900 mb-1">Your subscription has lapsed — your account has been moved to the Free tier.</div>
              <div className="text-sm text-ink-700">
                All your client records, sessions, photos, and clinical notes are intact and available. Reactivate any time to restore premium AI tools.
              </div>
            </div>
            <button onClick={ackDowngrade} className="p-2 rounded-full hover:bg-paper-100" data-testid="ack-downgrade-btn" aria-label="Dismiss">
              <X className="w-4 h-4 text-ink-500" />
            </button>
          </div>
        )}

        {/* Early Adopter badge */}
        {isEarlyAdopter && (
          <div data-testid="early-adopter-badge" className="rounded-3xl border border-sage-500/40 bg-sage-500/5 p-5 mb-6 flex items-start gap-3">
            <Crown className="w-5 h-5 text-sage-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-ink-900 mb-1">Early Adopter — {me.early_adopter.grant_type} access</div>
              <div className="text-sm text-ink-700">
                You've been granted full access to the <strong>{(tiers[me.early_adopter.tier] || {}).label || me.early_adopter.tier}</strong> tier.
                {me.early_adopter.expires_at ? ` Expires: ${new Date(me.early_adopter.expires_at).toLocaleDateString()}.` : " No expiration."}
                {me.early_adopter.reason ? ` (${me.early_adopter.reason})` : null}
              </div>
            </div>
          </div>
        )}

        {/* Current status card */}
        <div className="rounded-3xl border border-paper-200 bg-white p-5 sm:p-6 mb-8" data-testid="billing-current">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="label-overline mb-1">Current plan</div>
              <div className="font-heading text-2xl text-ink-900">
                {(tiers[effective] || {}).label || effective}
              </div>
              <div className="text-sm text-ink-700 mt-1">
                Status: <span className="font-medium">{isEarlyAdopter ? "Early Adopter" : (status || "free")}</span>
                {me.subscription?.billing_period && me.subscription.billing_period !== "none" && (
                  <span> · {me.subscription.billing_period}</span>
                )}
              </div>
              {me.quota?.unlimited ? (
                <div className="text-sm text-sage-700 mt-2 inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Unlimited AI usage
                </div>
              ) : (
                <div className="text-sm text-ink-700 mt-2">
                  AI usage: <strong>{me.quota.used_last_30_days}</strong> / {me.quota.quota_per_month} this month
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {me.verification?.status === "verified" ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sage-500/10 text-sage-700 text-xs font-medium" data-testid="verified-chip">
                  <ShieldCheck className="w-3.5 h-3.5" /> Verified professional
                </span>
              ) : (
                <Link
                  to="/billing/verify"
                  data-testid="verify-cta"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink-900 text-white text-xs font-medium hover:bg-ink-800"
                >
                  <ShieldCheck className="w-3.5 h-3.5" /> Submit verification
                </Link>
              )}
              {status === "active" && (
                <button
                  onClick={onCancel}
                  data-testid="cancel-sub-btn"
                  disabled={acting}
                  className="px-4 py-2 rounded-full border border-paper-200 text-xs hover:bg-paper-100"
                >
                  Cancel subscription
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Period toggle — in-app Square checkout is monthly-only */}
        {!inAppCheckout && (
        <div className="flex items-center justify-center gap-2 mb-6">
          <button
            data-testid="period-monthly"
            onClick={() => setPeriod("monthly")}
            className={`min-h-[40px] px-4 rounded-full text-sm transition ${period === "monthly" ? "bg-ink-900 text-white" : "bg-white border border-paper-200 text-ink-700 hover:bg-paper-100"}`}
          >
            Monthly
          </button>
          <button
            data-testid="period-annual"
            onClick={() => setPeriod("annual")}
            className={`min-h-[40px] px-4 rounded-full text-sm transition inline-flex items-center gap-2 ${period === "annual" ? "bg-ink-900 text-white" : "bg-white border border-paper-200 text-ink-700 hover:bg-paper-100"}`}
          >
            Annual <span className="px-1.5 py-0.5 rounded-full bg-sage-500/20 text-sage-700 text-[10px] font-semibold">save 17%</span>
          </button>
        </div>
        )}

        {/* Tier cards */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${tierOrder.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4`} data-testid="tier-grid">
          {tierOrder.map((code) => {
            const t = tiers[code] || {};
            const priceCents = (inAppCheckout || period === "monthly") ? t.monthly_price_cents : t.annual_price_cents;
            const isCurrent = effective === code;
            const isUpgrade = !isCurrent && code !== "free" && !isEarlyAdopter;
            const isFounding = code === "founding";
            return (
              <div
                key={code}
                data-testid={`tier-card-${code}`}
                className={`rounded-3xl border p-6 transition-all relative ${isCurrent ? "border-sage-500 bg-sage-500/5" : isFounding ? "border-ink-900 bg-white" : "border-paper-200 bg-white hover:border-paper-300"}`}
              >
                {isFounding && (
                  <span className="absolute -top-2.5 left-6 px-2.5 py-0.5 rounded-full bg-ink-900 text-white text-[10px] font-semibold uppercase tracking-wider" data-testid="founding-badge">Limited time</span>
                )}
                <div className="flex items-center justify-between mb-2">
                  <div className="font-heading text-xl text-ink-900">{t.label || code}</div>
                  {isCurrent && (
                    <span className="text-xs px-2 py-1 rounded-full bg-sage-500 text-white" data-testid={`tier-${code}-current`}>Current</span>
                  )}
                </div>
                <div className="text-3xl font-light text-ink-900 mb-1">{formatPrice(priceCents)}</div>
                <div className="text-xs text-ink-500 mb-5">
                  {priceCents ? `per ${(inAppCheckout || period === "monthly") ? "month" : "year"}` : "always"}
                  {isFounding && <span className="block mt-1 text-sage-700">Lock in this rate for as long as you stay subscribed.</span>}
                </div>
                <ul className="space-y-2 text-sm text-ink-800 mb-6" data-testid={`tier-${code}-features`}>
                  {Object.entries(t.features || {}).filter(([, on]) => on).map(([f]) => (
                    <li key={f} className="inline-flex items-start gap-2">
                      <Check className="w-4 h-4 text-sage-600 mt-0.5 shrink-0" />
                      <span>{featureLabel(f)}</span>
                    </li>
                  ))}
                  {t.ai_quota_per_month > 0 && (
                    <li className="inline-flex items-start gap-2">
                      <Check className="w-4 h-4 text-sage-600 mt-0.5 shrink-0" />
                      <span>{t.ai_quota_per_month} AI calls / month</span>
                    </li>
                  )}
                  {t.ai_quota_per_month === 0 && code !== "free" && (
                    <li className="inline-flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-sage-600 mt-0.5 shrink-0" />
                      <span>Unlimited AI usage</span>
                    </li>
                  )}
                </ul>
                {isCurrent ? (
                  <div className="text-center text-xs text-ink-500 py-3">You're on this plan</div>
                ) : code === "free" ? (
                  <div className="text-center text-xs text-ink-500 py-3">Always available</div>
                ) : (
                  <button
                    disabled={acting || !checkoutAvailable}
                    onClick={() => onSubscribe(code)}
                    data-testid={`subscribe-${code}-btn`}
                    className={`w-full min-h-[44px] rounded-full font-medium transition ${
                      checkoutAvailable ? "bg-ink-900 text-white hover:bg-ink-800" : "bg-paper-200 text-ink-500 cursor-not-allowed"
                    }`}
                    title={!checkoutAvailable ? "Online checkout will be enabled once payment provider is configured" : undefined}
                  >
                    {isUpgrade ? "Subscribe" : "Switch plan"}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {!checkoutAvailable && (
          <div className="text-xs text-ink-500 mt-4 text-center" data-testid="checkout-unavailable-note">
            Online checkout is currently in setup. Need access now? Contact an admin to grant your account.
          </div>
        )}

        <div className="mt-10 p-5 rounded-3xl bg-paper-100/60 text-xs text-ink-700">
          <strong className="text-ink-900">Why verification?</strong> Posturra's AI tools are designed for licensed manual-therapy professionals.
          Verification helps us maintain a clinical-grade community and is required before purchasing a paid plan.
        </div>
      </div>

      <SquareSubscribeModal
        open={!!modalTier}
        tier={modalTier}
        squareConfig={squareConfig}
        onClose={() => setModalTier(null)}
        onSuccess={async () => {
          setModalTier(null);
          await load();
          await refreshUser();
        }}
      />
    </Layout>
  );
}

function effectiveIsFounding(me) {
  return (me?.subscription?.tier === "founding") || me?.effective_tier === "founding";
}

function featureLabel(key) {
  const map = {
    ai_analysis: "AI posture analysis",
    ai_soap: "AI SOAP drafting",
    ai_wrap_up: "AI wrap-ups & home care",
    ai_protocols: "AI treatment protocols",
    clinical_snapshots: "Persistent clinical snapshots",
    continuity_progress: "Continuity of care tracking",
    photo_comparison: "Side-by-side photo comparison",
    anatomy_learning: "Interactive anatomy & learning mode",
    documentation: "Documentation tools",
    multi_practitioner: "Multiple practitioners",
  };
  return map[key] || key;
}
